import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getTemuWorkspaceToken,
  persistTemuWorkspaceToken,
  setTemuWorkspaceAuthRequiredHandler,
  temuPlatformAPI,
} from '@/temu-platform/api/temuPlatformApi';
import {
  IPE_TEMU_GATE_PATH,
  IPE_TEMU_BASE_PATH,
  TEMU_WORKSPACE_SHOP_TYPE_KEY,
  normalizeTemuShopType,
  parseTemuShopTypeSlugFromPath,
  shopTypeSlugForLabel,
  normalizeTemuShopTypeSlug,
} from '@/temu-platform/constants/temuRoutes';
import { allowDocumentUnload } from '@/temu-platform/hooks/allowDocumentUnload';
import { installWorkspaceShopTypeRedirect } from '@/utils/workspaceShopTypeRedirect';

const TemuWorkspaceAuthContext = createContext(null);

function readStoredShopType() {
  try {
    return localStorage.getItem(TEMU_WORKSPACE_SHOP_TYPE_KEY) || '';
  } catch {
    return '';
  }
}

function persistShopType(shopType) {
  try {
    const normalized = normalizeTemuShopType(shopType);
    if (normalized) {
      localStorage.setItem(TEMU_WORKSPACE_SHOP_TYPE_KEY, normalized);
    } else {
      localStorage.removeItem(TEMU_WORKSPACE_SHOP_TYPE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function TemuWorkspaceAuthProvider({ children }) {
  const [token, setToken] = useState(() => getTemuWorkspaceToken());
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);
  const [workspaceShopType, setWorkspaceShopType] = useState(() => readStoredShopType());

  const clearLocalSession = useCallback(() => {
    persistTemuWorkspaceToken(null);
    persistShopType('');
    setToken(null);
    setUnlocked(false);
    setExpiresAt(null);
    setWorkspaceShopType('');
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!token) {
      setUnlocked(false);
      setExpiresAt(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    try {
      const status = await temuPlatformAPI.getSessionStatus();
      setUnlocked(Boolean(status?.unlocked));
      setExpiresAt(status?.expiresAt || null);
      const type = normalizeTemuShopType(status?.shopType) || readStoredShopType();
      if (type) {
        setWorkspaceShopType(type);
        persistShopType(type);
      }
    } catch {
      setUnlocked(false);
      setExpiresAt(null);
      persistTemuWorkspaceToken(null);
      setToken(null);
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => installWorkspaceShopTypeRedirect({
    storageKey: TEMU_WORKSPACE_SHOP_TYPE_KEY,
    basePath: IPE_TEMU_BASE_PATH,
    parseSlugFromPath: parseTemuShopTypeSlugFromPath,
    shopTypeToSlug: (raw) => shopTypeSlugForLabel(raw) || normalizeTemuShopTypeSlug(raw),
  }), []);

  useEffect(() => {
    setTemuWorkspaceAuthRequiredHandler(() => {
      clearLocalSession();
      if (typeof window === 'undefined') return;
      const path = window.location.pathname || '';
      if (path.includes('/gate')) return;
      const redirect = `${path}${window.location.search || ''}`;
      const params = new URLSearchParams();
      if (redirect && redirect !== IPE_TEMU_GATE_PATH) {
        params.set('redirect', redirect);
      }
      params.set('shopAuthExpired', '1');
      allowDocumentUnload();
      window.location.replace(`${IPE_TEMU_GATE_PATH}?${params.toString()}`);
    });
    return () => setTemuWorkspaceAuthRequiredHandler(null);
  }, [clearLocalSession]);

  const unlock = useCallback(async (password, shopType) => {
    const normalizedType = normalizeTemuShopType(shopType) || '';
    const res = await temuPlatformAPI.unlockSession(password, normalizedType || undefined);
    const nextToken = res?.token;
    if (!nextToken) {
      throw new Error('验证失败');
    }
    const boundType = normalizeTemuShopType(res?.shopType) || normalizedType;
    persistTemuWorkspaceToken(nextToken);
    persistShopType(boundType);
    setToken(nextToken);
    setUnlocked(true);
    setExpiresAt(res?.expiresAt || null);
    setWorkspaceShopType(boundType);
    return res;
  }, []);

  const lock = useCallback(async () => {
    try {
      await temuPlatformAPI.lockSession();
    } catch {
      /* ignore */
    }
    clearLocalSession();
  }, [clearLocalSession]);

  const value = useMemo(
    () => ({
      unlocked,
      checking,
      expiresAt,
      workspaceShopType,
      unlock,
      lock,
      refreshStatus,
    }),
    [unlocked, checking, expiresAt, workspaceShopType, unlock, lock, refreshStatus],
  );

  return (
    <TemuWorkspaceAuthContext.Provider value={value}>
      {children}
    </TemuWorkspaceAuthContext.Provider>
  );
}

export function useTemuWorkspaceAuth() {
  const ctx = useContext(TemuWorkspaceAuthContext);
  if (!ctx) {
    throw new Error('useTemuWorkspaceAuth must be used within TemuWorkspaceAuthProvider');
  }
  return ctx;
}

export function useTemuWorkspaceAuthOptional() {
  return useContext(TemuWorkspaceAuthContext);
}
